import type { AuditRow } from "./auditTableState.ts";
import { UNKNOWN_EVENT_LABEL } from "../api/parse.ts";
import { formatSessionWhen } from "./sessionWhen.ts";

export const MISSING = "—";

export const AUDIT_DETAIL_FIELD_KEYS = [
  "eventId",
  "when",
  "who",
  "objectId",
  "whoSource",
  "type",
  "deviceName",
  "deviceId",
  "sessionId",
  "summary",
  "requestId",
] as const;

export type AuditDetailFieldKey = (typeof AUDIT_DETAIL_FIELD_KEYS)[number];

export type AuditDetailField = {
  key: AuditDetailFieldKey;
  label: string;
  value: string;
};

const SECRET_LABEL = /\b(token|secret|password|credential|sas|sig)\b/i;

export function dialogShowsSecretLikeLabel(text: string): boolean {
  return SECRET_LABEL.test(text);
}

function text(value: string | null | undefined): string {
  if (value === null || value === undefined) return MISSING;
  const trimmed = value.trim();
  return trimmed.length === 0 ? MISSING : trimmed;
}

function typeLabel(type: string): string {
  return type === "unknown" ? UNKNOWN_EVENT_LABEL : type;
}

function whoSourceLabel(source: AuditRow["whoSource"]): string {
  if (source === "snapshot") return "Snapshot";
  if (source === "directory") return "Directory";
  return MISSING;
}

export function auditDetailFields(
  row: AuditRow,
  summaryText: string,
  deviceName: string,
  timeZone?: string,
): AuditDetailField[] {
  const rows: AuditDetailField[] = [];
  for (const key of AUDIT_DETAIL_FIELD_KEYS) {
    switch (key) {
      case "eventId":
        rows.push({ key, label: "Event id", value: row.eventId });
        break;
      case "when":
        rows.push({ key, label: "When", value: formatSessionWhen(row.occurredAt, undefined, timeZone) });
        break;
      case "who":
        rows.push({ key, label: "Who", value: row.who });
        break;
      case "objectId":
        rows.push({ key, label: "Object id", value: text(row.whoGuid) });
        break;
      case "whoSource":
        rows.push({ key, label: "Who source", value: whoSourceLabel(row.whoSource ?? null) });
        break;
      case "type":
        rows.push({ key, label: "Type", value: typeLabel(row.type) });
        break;
      case "deviceName":
        rows.push({ key, label: "Device", value: deviceName });
        break;
      case "deviceId":
        rows.push({ key, label: "Device id", value: text(row.deviceId) });
        break;
      case "sessionId":
        rows.push({ key, label: "Session id", value: text(row.sessionId) });
        break;
      case "summary":
        rows.push({ key, label: "Summary", value: summaryText });
        break;
      case "requestId":
        rows.push({ key, label: "Request id", value: text(row.requestId) });
        break;
      default: {
        const _never: never = key;
        throw new Error(`Unhandled audit detail field ${_never}`);
      }
    }
  }
  return rows;
}
